/* AUTO-SYNCED from PsyKit.io/components/layout/NavLink.tsx — DO NOT EDIT. Regenerate with `npm run sync`. */
"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

/**
 * A single nav item with active-state awareness — the single source of truth for
 * the two interactions Jake asked for:
 *
 *   - hover  → the word shifts to the blue accent  (--color-accent-bright)
 *   - active → gold edge-glow (1px gold rim + soft gold halo) + gold word
 *
 * Change the look here and every nav that uses NavLink updates in lockstep.
 *
 * `exact` is required for "/" so Home isn't flagged active on every sub-route
 * (otherwise `"/features".startsWith("/")` would light Home up everywhere).
 */
export function NavLink({
  href,
  children,
  exact = false,
  onNavigate,
}: {
  href: string;
  children: React.ReactNode;
  exact?: boolean;
  /** Called after click — used by the mobile panel to close itself. */
  onNavigate?: () => void;
}) {
  const pathname = usePathname();
  const isActive = exact
    ? pathname === href
    : pathname === href || pathname.startsWith(`${href}/`);

  return (
    <Link
      href={href}
      aria-current={isActive ? "page" : undefined}
      onClick={onNavigate}
      className={[
        "relative rounded-md px-3 py-2 font-mono text-[12px] uppercase tracking-[0.14em]",
        "transition-all duration-150 motion-reduce:transition-none",
        isActive
          ? // ── ACTIVE: gold edge-glow ──────────────────────────────────────
            // 1px gold rim + soft gold halo. Tune the two shadow layers below to
            // make the glow tighter/wider or brighter/dimmer.
            "text-[var(--color-gold-soft)] bg-[var(--color-navy-mid)]/60 " +
            "shadow-[0_0_0_1px_var(--color-border-gold),0_0_16px_-3px_rgba(229,185,112,0.5)]"
          : // ── IDLE → HOVER: blue word shift ───────────────────────────────
            "text-[var(--color-text-muted)] hover:text-[var(--color-accent-bright)] " +
            "hover:bg-[var(--color-navy-mid)]/50",
      ].join(" ")}
    >
      {children}
    </Link>
  );
}
